import BackendQuery from 'decentralized/BackendQuery.js';
import {isNumber} from 'utils/IsNumber.js';
import accounts from 'accounts/Accounts.js';
import ethereum from 'decentralized/ethereum.js';
import {getReleases} from 'decentralized/artists/Artists.js';
import {getRatings} from 'utils/getRatings.js';
import {getBlockDate} from 'utils/getBlockDate.js';
import GuildSamplesContract from 'decentralized/contracts/GuildSamplesContract.js';
import {
    SampleCreated,
    SampleYoutube,
    NewDiscogsSample,
    SampleTagged
} from 'decentralized/EthereumEvents.js';

let contractAddress = new GuildSamplesContract().contractAddress;
const blocksPerDay = 6425;
const cache = {};

/**
 * Want to return a list of grouped samples, grouped by:
 ** day
 ** source
 ** tag
 
 * First need to get recent results paginated for a certain range
 * 
*/

export const getSoundResultsServer = (pageSize=50, searchTerms, guildId=0, groupBy, start, filterFavorites, sourceToSearch, tag, packToSearch) => {
    let year = undefined;
    if (isNumber(searchTerms)) {
        let _year = parseInt(year);
        if (_year > 1945 && _year < 2200) {
            year = _year
        } 
    }
    return new Promise(async (resolve, reject) => {
        let {releaseId, videoId} = sourceToSearch || {};
        let guildIds = guildId !== 0 ? [guildId] : await ethereum.getGuildsWithAccess();
        let pack = packToSearch ? packToSearch.packHash : undefined;
        let userId = await accounts.getAccount();
        let body = JSON.stringify({
            size: Math.floor(pageSize),
            pack,
            searchTerm: searchTerms,
            guildIds: guildIds,
            groupBy,
            year,
            start: Math.floor(start),
            filterFavorites,
            releaseId,
            videoId,
            tag,
            user: userId
        });
        fetch('/searchByDay/' , {
                method: 'POST',
            body,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }
        })
            .then(resp => resp.json())
            .then(resp => {
                resp.sort((a,b) => b.blockNumber - a.blockNumber)
                resolve(resp);
            });
    });
}

export const getSoundResults = async (searcher, searchTerms, len=8000, guildIds=[0]) => {
    if (searchTerms in cache) {
        return cache[searchTerms];
    }

    let recentSounds =  await getRecentSounds(searcher, searchTerms, len, guildIds);
    if (recentSounds.length === 0) {
        return [];
    }
    let recentDiscogs =  await getRecentDiscogs(recentSounds, len, guildIds);
    let recentReleases =  await getRecentReleases(recentDiscogs);
    let recentYoutubes =  await getRecentYoutubeSamples(recentSounds, len, guildIds);
    let recentTags =  await getRecentTags(recentSounds, len*3, guildIds);
    let ratings = await getRatings(recentSounds.map(x => x.ipfsHash));
    let sounds = combineAll(recentSounds, recentDiscogs, recentYoutubes, recentTags, ratings, recentReleases);

    let byDay = getByDay(sounds);
    let byDayAndSource = partitionBySource(byDay, searchTerms);
    let x = await fetchDaysForBlocks(byDayAndSource);
    cache[searchTerms] = x;
    return x;
};

export const getRecentSounds = async (searcher, searchTerm, len=500, guildIds) => {
    let soundIds = undefined;
    let filterByTitle = false;
    if (searchTerm.trim() !== "") {
        let searchResults = await searcher.freeTextSearch(searchTerm);
        let tags = searchResults.TAG.map(x => x.value);
        if (tags.length === 0) {
            filterByTitle = true;
        } else {
            soundIds = await getSoundsWithOrTags(tags);
            if (soundIds.length === 0) {
                return [];
            }
        }
    }

    let query = new BackendQuery(contractAddress)
        .from(SampleCreated)
        .select("ipfsHash")
        .select("user")
        .select("title")
        .whereIn("guildId", guildIds)
        .limit(len);

    if (soundIds) {
        query.whereIn("ipfsHash", soundIds);
    }

    let ret = await query.executeQuery();
    if (filterByTitle) {
        searchTerm = searchTerm.toLowerCase();
        return ret.filter(
            x => x.title.toLowerCase().includes(searchTerm));
    }
    return ret;
};

export const getRecentDiscogs = async (recentSounds, len=500, guildIds) => {
    let query = new BackendQuery(contractAddress)
        .from(NewDiscogsSample)
        .select("sampleHash")
        .select("coverArtHash")
        .select("discogsId")
        .whereIn("guildIds", guildIds)
        .whereIn("sampleHash", recentSounds.map(x => x.ipfsHash))
        .limit(len);

    return await query.executeQuery();
};

export const getRecentYoutubeSamples = async (recentSounds, len=500, guildIds) => {
    let query = new BackendQuery(contractAddress)
        .from(SampleYoutube)
        .select("ipfsHash")
        .select("videoId")
        .whereIn("guildIds", guildIds)
        .whereIn("ipfsHash", recentSounds.map(x => x.ipfsHash))
        .limit(len);

    return await query.executeQuery();
};

export const getRecentTags = async (recentSounds, len=500, guildIds) => {
    let query = new BackendQuery(contractAddress)
        .from(SampleTagged)
        .select("ipfsHash")
        .select("tag")
        .whereIn("guildIds", guildIds)
        .whereIn("ipfsHash", recentSounds.map(x => x.ipfsHash))
        .limit(len);

    let results = await query.executeQuery();
    let tags = {};
    for (let result of results) {
        if (!(result.ipfsHash in tags)) {
            tags[result.ipfsHash] = [];
        }
        tags[result.ipfsHash].push(result.tag);
    }
    for (let key in tags) {
        tags[key] = Array.from(new Set(tags[key]));
    }
    return tags;
};

const combineAll = (sampleCreated, newDiscogs, sampleYoutube, tags, ratings, recentReleases) => {
    let combined = {};
    for (let result of sampleCreated) {
        combined[result.ipfsHash] = {
            ... result,
            tags: tags[result.ipfsHash] || [],
            rating: ratings[result.ipfsHash]
        };
    }

    for (let ipfsHash in recentReleases) {
        combined[ipfsHash] = {
            ... combined[ipfsHash],
            ... recentReleases[ipfsHash]
        };
    }

    for (let result of newDiscogs) {
        combined[result.sampleHash] = {
            ... combined[result.sampleHash],
            ... result
        };
    }

    for (let result of sampleYoutube) {
        combined[result.ipfsHash] = {
            ... combined[result.ipfsHash],
            ... result
        };
    }

    let results = [];
    for (let result of sampleCreated) {
        results.push(combined[result.ipfsHash]);
    }

    return results;
}

export const getByDay = (results) => {
    // first try to find blocks that are within a certain range
    let blocks = [];
    for (let result of results) {
        let {blockNumber} = result;
        let lastBlock = blocks[blocks.length - 1];
        if (lastBlock === undefined) {
            blocks.push({
                blockNumber,
                results: [result]
            });
        } else {
            let lastBlockNumber = lastBlock.blockNumber;
            if (Math.abs(lastBlockNumber - blockNumber) <= blocksPerDay) {
                lastBlock.results.push(result);
            } else {
                blocks.push({
                    blockNumber,
                    results: [result]
                });
            }
        }
    }
    return blocks;
};

export const partitionBySource = (byDay, searchTerm) => {
    for (let dayResult of byDay) {
        let {results} = dayResult;
        let discogs = {};
        let instrumentTypes = {};
        let youtube = {};
        let unsourced = {};
        for (let result of results) {
            if (result.discogsId) {
                if (discogs[result.coverArtHash] === undefined) {
                    discogs[result.coverArtHash] = [];
                } else {
                }
                discogs[result.coverArtHash].push(result);
            } else if (result.videoId) {
                if (youtube[result.videoId] === undefined) {
                    youtube[result.videoId] = [];
                }
                youtube[result.videoId].push(result);
            } else {
                let tag = result.tags[0] || '';
                if (searchTerm !== '' && result.tags.some(tag => !tag.toLowerCase().includes(searchTerm.toLowerCase()))) {
                    tag = result.tags.find(tag => !tag.toLowerCase().includes(searchTerm.toLowerCase()) &&
                                          tag !== 'Resampled');
                    if (tag === undefined) {
                        tag = result.tags[0];
                    }
                }
                if (unsourced[tag] === undefined) {
                    unsourced[tag] = [];
                }
                unsourced[tag].push(result);

                if (result.instrumentType) {
                    if (instrumentTypes[result.instrumentType] === undefined) {
                        instrumentTypes[result.instrumentType] = [];
                    }
                    instrumentTypes[result.instrumentType].push(result);
                }
            }
        }
        dayResult.discogs = discogs;
        dayResult.youtube = youtube;
        dayResult.unsourced = unsourced;
        dayResult.instrumentTypes = instrumentTypes;
    }
    return byDay;
};

export const fetchDaysForBlocks = async (byDayAndSource) => {
    for (let i in byDayAndSource) {
        let result = byDayAndSource[i];
        /*
        let day = new Date(); //await getBlockDate(parseInt(result.blockNumber));
        result.date = day;
        */
    }
    return byDayAndSource;
};

const getSoundsWithOrTags = async (tags) => {
    let soundIds = [];
    let query = new BackendQuery(contractAddress)
        .from(SampleTagged)
        .select("ipfsHash")
        .whereIn("tag", tags);
    let results = await query.executeQuery();
    return results.map(x => x.ipfsHash);
};

const getRecentReleases = async (recentDiscogs) => {
    let discogIds = recentDiscogs.map(x => x.discogsId);
    let releases = await getReleases(discogIds);
    let results = {};
    for (let result of recentDiscogs) {
        results[result.sampleHash] = releases[result.discogsId];
    }
    return results;
}
